import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { RiEyeLine, RiPencilLine, RiRobot2Line, RiShieldCheckLine } from '@remixicon/react'
import { Alert, Button, Card, SectionSpinner } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'

interface ClientInfo {
  client_id: string
  client_name: string
  scope: string
}

/**
 * Where the OAuth flow becomes a decision a person makes.
 *
 * The backend sent the browser here because it has no session of its own — the
 * operator's credential lives in this app. So this page identifies them, shows
 * who is asking and what it will be able to do, and only on approval asks the
 * backend for the code that goes back to the client.
 */
export default function OAuthConsent() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [params] = useSearchParams()

  const [client, setClient] = useState<ClientInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [approving, setApproving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clientId = params.get('client_id')
  const redirectUri = params.get('redirect_uri')
  const codeChallenge = params.get('code_challenge')

  useEffect(() => {
    if (!clientId) {
      setError(t('consent.missingRequest'))
      setLoading(false)
      return
    }

    api
      .get<{ data: ClientInfo }>(`/api/oauth/client/${clientId}`)
      .then((response) => setClient(response.data))
      .catch((err) => setError(toErrorMessage(err) ?? t('errors.generic')))
      .finally(() => setLoading(false))
  }, [clientId, t])

  const decide = async (approve: boolean) => {
    if (!redirectUri) return

    if (!approve) {
      // Denial goes back to the client as OAuth expects, rather than leaving it
      // waiting for a callback that never comes.
      const url = new URL(redirectUri)
      url.searchParams.set('error', 'access_denied')
      if (params.get('state')) url.searchParams.set('state', params.get('state')!)
      window.location.href = url.toString()
      return
    }

    setApproving(true)
    setError(null)
    try {
      const response = await api.post<{ redirect_to: string }>('/api/oauth/authorize', {
        client_id: clientId,
        redirect_uri: redirectUri,
        code_challenge: codeChallenge,
        code_challenge_method: params.get('code_challenge_method') ?? 'S256',
        state: params.get('state'),
        resource: params.get('resource'),
      })
      // A full navigation, not a router push: the destination belongs to the
      // client, not to this app.
      window.location.href = response.redirect_to
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
      setApproving(false)
    }
  }

  if (loading) return <SectionSpinner />

  const usable = client && redirectUri && codeChallenge

  /**
   * Where the authorization code will actually be delivered.
   *
   * The name above it is whatever the client called itself at registration,
   * which anyone can set — so it is not, on its own, something to trust. This
   * is: it is the address the credential leaves for, and the one thing on this
   * screen an operator can check against what they just did.
   */
  let destination: string | null = null
  if (redirectUri) {
    try {
      destination = new URL(redirectUri).host
    } catch {
      destination = redirectUri
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-lg items-center px-4 py-12">
      <Card className="w-full p-8">
        <div className="flex flex-col items-center text-center">
          <span className="flex size-12 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400">
            <RiRobot2Line className="size-6" aria-hidden="true" />
          </span>
          <h1 className="mt-4 text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-50">
            {t('consent.title', { client: client?.client_name ?? t('consent.anAgent') })}
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {t('consent.subtitle', { email: user?.email ?? '' })}
          </p>
        </div>

        {error && <Alert className="mt-6">{error}</Alert>}

        {usable && (
          <>
            <div className="mt-6 space-y-3 rounded-md border border-gray-200 p-4 dark:border-gray-800">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
                {t('consent.willBeAbleTo')}
              </p>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <RiEyeLine
                    className="mt-0.5 size-4 shrink-0 text-gray-400 dark:text-gray-500"
                    aria-hidden="true"
                  />
                  <span>{t('consent.canRead')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <RiPencilLine
                    className="mt-0.5 size-4 shrink-0 text-gray-400 dark:text-gray-500"
                    aria-hidden="true"
                  />
                  <span>{t('consent.canDraft')}</span>
                </li>
              </ul>
            </div>

            <div className="mt-4 flex items-start gap-2 rounded-md bg-emerald-50 p-4 text-sm text-emerald-900 dark:bg-emerald-500/10 dark:text-emerald-200">
              <RiShieldCheckLine className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>{t('consent.cannotPublish')}</span>
            </div>

            <div className="mt-4 rounded-md border border-gray-200 p-4 dark:border-gray-800">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('consent.sendsCodeTo')}
              </p>
              <p className="mt-1 break-all font-mono text-sm text-gray-900 dark:text-gray-50">
                {destination}
              </p>
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                {t('consent.nameIsSelfReported')}
              </p>
            </div>

            <div className="mt-6 flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => decide(false)}
                disabled={approving}
              >
                {t('consent.deny')}
              </Button>
              <Button className="flex-1" onClick={() => decide(true)} loading={approving}>
                {t('consent.approve')}
              </Button>
            </div>

            <p className="mt-4 text-center text-xs text-gray-400 dark:text-gray-500">
              {t('consent.revokeLater')}
            </p>
          </>
        )}
      </Card>
    </div>
  )
}
